/**
 * Gráficos do Painel de Assistências que vieram da primeira versão da tela
 * e seguem valendo no v2: donut de motivos, horários de pico e heatmap de
 * calendário. Cada componente recebe uma fatia do payload e renderiza o
 * corpo do card (.cc__body). Escopo .fin2 + recharts.
 */
import React from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";

import { VazioInline } from "../_shared/Estados";
import { TOOLTIP_TEMA } from "./_blocos";

const CORES = [
  "var(--chart-3)", "var(--chart-2)", "var(--chart-5)", "var(--chart-4)",
  "var(--chart-1)", "var(--chart-6)", "var(--chart-7)",
];

function n(v) { return Number(v || 0); }

// ---------------------------------------------------------------- Motivos

export function MotivosDonut({ motivos }) {
  const data = (motivos || []).map((m, i) => ({
    name: m.label || m.codigo, value: n(m.total), fill: CORES[i % CORES.length],
  }));
  const total = data.reduce((a, d) => a + d.value, 0);
  if (total === 0) return <VazioInline>Sem itens no período</VazioInline>;
  return (
    <div style={{ display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
      <div style={{ position: "relative", width: 150, height: 150, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%"
                 innerRadius={48} outerRadius={70} paddingAngle={2}>
              {data.map((e) => <Cell key={e.name} fill={e.fill} />)}
            </Pie>
            <Tooltip {...TOOLTIP_TEMA} formatter={(v, nm) => [`${v} item(ns)`, nm]} />
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", textAlign: "center", pointerEvents: "none" }}>
          <div>
            <div className="mono" style={{ fontSize: 16, fontWeight: 700 }}>{total}</div>
            <div className="tiny muted">itens</div>
          </div>
        </div>
      </div>
      <div style={{ flex: 1, minWidth: 140 }}>
        {data.map((d) => (
          <div key={d.name} className="flex between" style={{ fontSize: 12.5, padding: "3px 0" }}>
            <span className="ell">
              <span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: d.fill, marginRight: 6 }} />
              {d.name}
            </span>
            <strong className="mono">{Math.round((d.value / total) * 100)}%</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- horários de pico

export function HorariosPico({ horarios }) {
  const data = (horarios || []).map((h) => ({
    hora: String(h.hora).padStart(2, "0"), total: n(h.total),
  }));
  const soma = data.reduce((a, d) => a + d.total, 0);
  if (soma === 0) return <VazioInline>Sem registros de horário no período</VazioInline>;
  const pico = Math.max(...data.map((d) => d.total));
  return (
    <ResponsiveContainer width="100%" height={200}>
      <BarChart data={data} margin={{ top: 6, right: 6, left: -4, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--divider)" vertical={false} />
        <XAxis dataKey="hora" tick={{ fontSize: 9, fill: "var(--ink-3)" }} interval={1} />
        <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "var(--ink-3)" }} width={36} />
        <Tooltip {...TOOLTIP_TEMA} formatter={(v) => [`${v} solicitação(ões)`, "Total"]} labelFormatter={(l) => `${l}h`} />
        <Bar dataKey="total" radius={[3, 3, 0, 0]}>
          {data.map((d) => (
            <Cell key={d.hora} fill={d.total === pico ? "var(--accent)" : "var(--chart-2)"} />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ---------------------------------------------------------------- heatmap de calendário

const MESES_PT = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
const DIAS_SEMANA = ["D", "S", "T", "Q", "Q", "S", "S"];

// Nível de cor por densidade (single-hue accent, 4 níveis + vazio).
function corDensidade(total, max) {
  if (!total) return "var(--surface-2)";
  const t = total / max;
  const pct = t <= 0.25 ? 28 : t <= 0.5 ? 48 : t <= 0.75 ? 70 : 100;
  return `color-mix(in srgb, var(--accent) ${pct}%, var(--surface-2))`;
}

// "2026-09-18" -> Date LOCAL (new Date("2026-09-18") seria meia-noite UTC e
// cairia no dia anterior em São Paulo).
function dataLocal(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ""));
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : null;
}

/** `de`/`ate` = limites do recorte (ISO date-only) que o backend devolve. */
export function CalendarioHeatmap({ calendario, de, ate }) {
  const mapa = new Map((calendario || []).map((c) => [c.dia, n(c.total)]));
  const total = [...mapa.values()].reduce((a, v) => a + v, 0);
  if (total === 0) return <VazioInline>Sem solicitações no período</VazioInline>;

  const fim = dataLocal(ate) || new Date();
  fim.setHours(0, 0, 0, 0);
  const inicio = dataLocal(de) || new Date(fim.getFullYear(), fim.getMonth(), fim.getDate() - 89);
  // Recua até o domingo anterior para alinhar as colunas por semana.
  const start = new Date(inicio); start.setDate(inicio.getDate() - inicio.getDay());

  const dias = [];
  for (let d = new Date(start); d <= fim; d.setDate(d.getDate() + 1)) {
    dias.push(new Date(d));
  }
  const max = Math.max(1, ...mapa.values());

  const CELL = 13, GAP = 3, TOPO = 16, ESQ = 16;
  const semanas = Math.ceil(dias.length / 7);
  const largura = ESQ + semanas * (CELL + GAP);
  const altura = TOPO + 7 * (CELL + GAP);

  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

  // Rótulos de mês (na 1ª semana em que o mês aparece).
  const rotulosMes = [];
  let ultimoMes = -1;
  dias.forEach((d, i) => {
    if (d.getDay() !== 0) return;
    if (d.getMonth() !== ultimoMes) {
      ultimoMes = d.getMonth();
      rotulosMes.push({ x: ESQ + Math.floor(i / 7) * (CELL + GAP), label: MESES_PT[d.getMonth()] });
    }
  });

  // viewBox + width 100%: o SVG encolhe para caber (zero scroll lateral),
  // sem passar do tamanho natural em telas largas.
  return (
    <div>
      <svg viewBox={`0 0 ${largura} ${altura}`} role="img"
           aria-label="Densidade de solicitações por dia"
           style={{ display: "block", width: "100%", maxWidth: largura, height: "auto" }}>
        {rotulosMes.map((r, i) => (
          <text key={i} x={r.x} y={11} fontSize="9" fill="var(--ink-3)">{r.label}</text>
        ))}
        {DIAS_SEMANA.map((lbl, wd) => (
          wd % 2 === 1 ? (
            <text key={wd} x={0} y={TOPO + wd * (CELL + GAP) + CELL - 2}
                  fontSize="8" fill="var(--ink-4)">{lbl}</text>
          ) : null
        ))}
        {dias.map((d, i) => {
          if (d < inicio) return null;
          const t = mapa.get(iso(d)) || 0;
          const col = Math.floor(i / 7);
          const row = d.getDay();
          return (
            <rect key={i}
                  x={ESQ + col * (CELL + GAP)} y={TOPO + row * (CELL + GAP)}
                  width={CELL} height={CELL} rx={2.5}
                  fill={corDensidade(t, max)}
                  stroke="var(--border-subtle)" strokeWidth="0.5">
              <title>{`${d.toLocaleDateString("pt-BR")}: ${t} solicitação(ões)`}</title>
            </rect>
          );
        })}
      </svg>
      <div className="legend-row" style={{ marginTop: 10, gap: 6, alignItems: "center" }}>
        <span className="tiny muted">menos</span>
        {[0, 0.25, 0.6, 0.85, 1].map((f, i) => (
          <span key={i} style={{ width: 12, height: 12, borderRadius: 2.5, background: corDensidade(f * max, max), border: "0.5px solid var(--border-subtle)" }} />
        ))}
        <span className="tiny muted">mais</span>
      </div>
    </div>
  );
}
